/**
 * Chuỗi công đoạn của một đơn gia công (FB-GSULC8): một đơn có thể gửi nhiều công đoạn liên tiếp
 * (VD Cán màng → Kéo lụa → Bế). `routingStepIds` giữ thứ tự chuỗi; `routingStepId` luôn là công đoạn đầu
 * để các màn hình / báo cáo cũ chỉ đọc một công đoạn vẫn đúng.
 */
export interface OutsourceStepRef {
  routingStepIds?: string[] | null;
  routingStepId?: string | null;
  productionOrderId?: string | null;
  processId?: string | null;
}

/** Danh sách id công đoạn của đơn (bỏ trùng, giữ thứ tự); đơn cũ chỉ có routingStepId */
export function outsourceStepIds(po: OutsourceStepRef): string[] {
  const ids = (po.routingStepIds || []).filter(Boolean);
  if (ids.length === 0 && po.routingStepId) ids.push(po.routingStepId);
  return Array.from(new Set(ids));
}

/**
 * Chuẩn hoá dữ liệu ghi xuống DB từ input form: nhận `routingStepIds` (mới) hoặc `routingStepId` (cũ).
 * Trả về undefined khi input không nhắc tới công đoạn (để lệnh cập nhật không xoá chuỗi đang có).
 */
export function normalizeOutsourceStepInput(input: {
  routingStepIds?: string[] | null;
  routingStepId?: string | null;
}): { routingStepIds: string[]; routingStepId: string | null } | undefined {
  if (input.routingStepIds === undefined && input.routingStepId === undefined) return undefined;
  const ids = Array.from(
    new Set(
      (input.routingStepIds ?? (input.routingStepId ? [input.routingStepId] : [])).filter(
        (id): id is string => typeof id === 'string' && id.length > 0,
      ),
    ),
  );
  return { routingStepIds: ids, routingStepId: ids[0] ?? null };
}

/**
 * Đổi số cái nhận về sang đơn vị của công đoạn: công đoạn chạy theo tờ (In, Cán màng, Bế…) ghi số tờ = số cái / số con trên tờ (ups),
 * làm tròn lên (tờ có con lỗi vẫn là tờ đã gia công). Công đoạn theo cái hoặc LSX không có ups giữ nguyên số cái.
 */
export function outsourceOutputInStepUnit(
  qty: { ok: number; ng: number },
  unit: string,
  ups?: number | null,
): { ok: number; ng: number } {
  const perSheet = Number(ups) || 0;
  if (unit !== 'SHEET' || perSheet <= 1) return qty;
  const total = Math.ceil((qty.ok + qty.ng) / perSheet);
  const ng = Math.min(total, Math.ceil(qty.ng / perSheet));
  return { ok: total - ng, ng };
}

type OutsourceLine = { quantity?: unknown; unitPrice?: unknown; amount?: unknown };

/** Tổng tiền các dòng đơn gia công (materialIssued): thành tiền dòng, hoặc số lượng × đơn giá khi dòng chưa có thành tiền */
export function outsourceLinesAmount(lines: unknown): number {
  if (!Array.isArray(lines)) return 0;
  return (lines as OutsourceLine[]).reduce((sum, l) => {
    const amount = Number(l?.amount);
    return sum + (Number.isFinite(amount) && amount > 0 ? amount : (Number(l?.quantity) || 0) * (Number(l?.unitPrice) || 0));
  }, 0);
}

/**
 * Đơn giá đầu đơn gia công. Đơn nhiều dòng giá khác nhau: tổng tiền các dòng / tổng SL đặt
 * (trước đây form gửi 0 → tổng tiền 0 và nghiệm thu không ghi công nợ NCC — đi chuỗi gia công 03/11/2026).
 */
export function outsourceHeaderUnitPrice(qtyOrdered: number, unitPrice: number, lines: unknown): number {
  const linesAmount = outsourceLinesAmount(lines);
  if (Array.isArray(lines) && lines.length > 1 && linesAmount > 0 && Number(qtyOrdered) > 0) {
    return linesAmount / Number(qtyOrdered);
  }
  return Number(unitPrice) || 0;
}

/** Tiền công phần đạt của một lần nghiệm thu: đơn nhiều dòng chia tổng tiền các dòng theo tỉ lệ SL đạt / SL đặt, làm tròn đồng */
export function outsourceAcceptedAmount(
  po: { qtyOrdered?: number | null; unitPrice?: unknown; materialIssued?: unknown },
  acceptedQty: number,
): number {
  const unitValue = outsourceHeaderUnitPrice(Number(po.qtyOrdered) || 0, Number(po.unitPrice) || 0, po.materialIssued);
  return Math.round((Number(acceptedQty) || 0) * unitValue);
}

/**
 * Các công đoạn LSX của đơn gia công, theo thứ tự công nghệ (stepOrder).
 * Đơn chưa gắn công đoạn nhưng có LSX + mã công đoạn (processId): lấy công đoạn LSX cùng mã.
 * @param db Prisma client hoặc transaction client
 */
export async function findOutsourceRoutingSteps(db: any, po: OutsourceStepRef): Promise<any[]> {
  const ids = outsourceStepIds(po);
  if (ids.length > 0) {
    return db.routingStep.findMany({
      where: { id: { in: ids }, isDeleted: false },
      orderBy: { stepOrder: 'asc' },
    });
  }
  if (po.productionOrderId && po.processId) {
    const step = await db.routingStep.findFirst({
      where: { productionOrderId: po.productionOrderId, stepId: po.processId, isDeleted: false },
    });
    return step ? [step] : [];
  }
  return [];
}

/**
 * SL còn nhận được của đơn gia công (04/11/2026): hàng lỗi trả nhà gia công làm bù trên cùng đơn nên không chiếm chỗ —
 * trần = max(số đã gửi, SL đặt + hao hụt cho phép) − (đã nhận − đã loại).
 */
export function outsourceReceivableQty(po: { qtySentTotal?: number | null; qtyOrdered?: number | null; allowanceQty?: number | null; qtyReceived?: number | null; qtyRejected?: number | null }) {
  const cap = Math.max(Number(po?.qtySentTotal) || 0, (Number(po?.qtyOrdered) || 0) + (Number(po?.allowanceQty) || 0));
  const netReceived = Math.max(0, (Number(po?.qtyReceived) || 0) - (Number(po?.qtyRejected) || 0));
  return cap > 0 ? Math.max(0, cap - netReceived) : Infinity;
}
